import { useState, useCallback } from 'react'
import { ARTIST_SEARCH_ENDPOINT } from '@/config/endpoints'
import type { Artist } from '@/types/models'
import { useAuthState } from './useAuth'
import { isCountrySentinel, resolveSentinelToCities } from '@/lib/locationRegions'

interface SearchParams {
  query: string
  /** Takes precedence over `city` when non-empty */
  cities?: string[]
  city?: string
}

export function useArtistSearch() {
  const { user } = useAuthState()
  const [searchResults, setSearchResults] = useState<Artist[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  const performSearch = useCallback(async (params: SearchParams) => {
    if (!user) {
      setSearchError('User not authenticated')
      return
    }

    if (!params.query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    setSearchError(null)

    try {
      const idToken = await user.getIdToken()
      
      const response = await fetch(ARTIST_SEARCH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify((() => {
          const payload: Record<string, unknown> = {
            query: params.query.trim(),
          }
          const geo = params.cities?.filter((c) => c.trim())
          if (geo?.length) {
            payload.cities = geo
            return payload
          }
          if (!params.city) return payload
          if (isCountrySentinel(params.city)) {
            const cities = resolveSentinelToCities(params.city)
            if (cities?.length) payload.cities = cities
          } else {
            payload.city = params.city
          }
          return payload
        })()),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Search failed with status ${response.status}`)
      }

      const data = await response.json()
      setSearchResults(data.results || [])
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Search failed'
      setSearchError(errorMessage)
      console.error('[useArtistSearch] Error:', errorMessage)
    } finally {
      setIsSearching(false)
    }
  }, [user])

  const clearSearch = useCallback(() => {
    setSearchResults([])
    setSearchError(null)
  }, [])

  return {
    searchResults,
    isSearching,
    searchError,
    performSearch,
    clearSearch,
  }
}

